/* @flow */
import { cloneDeep } from 'lodash';

import ActivityRecipient from 'entities/ActivityRecipient';
import ActivityRecipients from 'collections/ActivityRecipients';
import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';
import Group from 'entities/Group';
import RecipientSummary from 'entities/RecipientSummary';
import RelatedUserSummary from 'entities/RelatedUserSummary';
import jstz from 'jstimezonedetect';

import type { ActivityCreateParams, ActivityEditParams } from 'payments/constants/ActivityTypes';

export const ATTENDANCE = {
  YES: 'yes',
  NO: 'no',
  MAYBE: 'maybe',
  PENDING: 'pending',
};

export const SHARE_MODAL = 'share_modal';

export const TYPES = {
  FIELD_TRIP: 'field_trip',
  ORDER: 'order',
  EVENT: 'event',
  FUNDRAISER: 'fundraiser',
};

export type GroupAvatar = {
  color: string,
  file_url: string,
  id: number,
  svg_url: string,
};

export type ActivityGroupSummary = {
  class_name: string,
  group_avatar: GroupAvatar,
  id: number,
  name: string,
  uuid: string,
};

export type ActivityTypeType = 'field_trip' | 'order' | 'event' | 'fundraiser';

export const ACTIVITY_PRINTED_MSG = 'We’ve emailed you the Activity record you’ve requested. You should receive it within 10 minutes.';


class ActivityCover {
  image: any;
  color: string;

  constructor(image: any, color: string) {
    this.image = image;
    this.color = color;
  }
}

const COVERS = {
  field_trip: new ActivityCover(require('assets/images/payments/covers/field-trip.png'), '#F4FBFF'),
  order: new ActivityCover(require('assets/images/payments/covers/order.png'), '#F8F8F8'),
  event: new ActivityCover(require('assets/images/payments/covers/event.png'), '#EFF9F8'),
  fundraiser: new ActivityCover(require('assets/images/payments/covers/fundraiser.png'), '#E9FBF7'),
};

const THUMBNAILS = {
  field_trip: new ActivityCover(require('assets/images/payments/thumbnails/field-trip.png'), '#F4FBFF'),
  order: new ActivityCover(require('assets/images/payments/thumbnails/order.png'), '#F8F8F8'),
  event: new ActivityCover(require('assets/images/payments/thumbnails/event.png'), '#EFF9F8'),
  fundraiser: new ActivityCover(require('assets/images/payments/thumbnails/fundraiser.png'), '#E9FBF7'),
};

const DEFAULT_COVER = COVERS['fundraiser'];
const DEFAULT_THUMBNAIL = THUMBNAILS['fundraiser'];

class Activity extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
  }

  defaults() {
    return {
      amount_per_person: 0,
      is_sender: false,
      requester: new RelatedUserSummary(),
      title: '',
      transactions_count: 0,
      group_summaries: [],
    };
  }

  getPayUrl(): string {
    return this.get('pay_url');
  }

  getGroupSummaries(): ?ActivityGroupSummary[] {
    return this.get('group_summaries');
  }

  getRequester(): RelatedUserSummary {
    return this.get('requester');
  }

  getEventType() {
    return this.get('event_type');
  }

  getCover(): ActivityCover {
    const eventType = this.getEventType();
    return COVERS[eventType] || DEFAULT_COVER;
  }

  getThumbnail(): ActivityCover {
    const eventType = this.getEventType();
    return THUMBNAILS[eventType] || DEFAULT_THUMBNAIL;
  }

  getAmountPerPerson(): ?number {
    return this.get('amount_per_person');
  }

  hasChildren(): boolean {
    return !!this.get('has_children');
  }

  hasDisableReplies(): boolean {
    return !!this.get('disable_replies');
  }

  isOrHasCollectedMoneyOnline(): boolean {
    return this.isCollectingMoneyOnline() || this.getTransactionsCount() > 0;
  }

  isCollectingMoneyOnline(): boolean {
    return !this.isFree() && this.get('collect_online');
  }

  isInGroup(group: Group): boolean {
    return group.isRoot() || !!this.get('group_summaries').find(gs => gs.name === group.getCode());
  }

  isFree(): boolean {
    return !this.getAmountPerPerson();
  }

  getTotalCollected() {
    return this.get('total_collected');
  }

  getTotalDeposited() {
    return this.get('total_deposited');
  }

  getCurrentBalance() {
    return this.get('current_balance');
  }

  getTransactionsCount(): number {
    return this.get('transactions_count');
  }

  getRespondedUsersSample() {
    return this.get('responded_users_sample') || [];
  }

  isSender(): boolean {
    return this.get('is_sender');
  }

  isClosed(): boolean {
    return this.get('state') === 'closed';
  }

  isBlocked(): boolean {
    return this.get('state') === 'blocked';
  }

  hasPaid(): boolean {
    return this.get('has_paid');
  }

  hasResponded(): boolean {
    return this.get('has_responded');
  }

  getViewingUserResponse(): ?ActivityRecipient {
    console.error('undefined getViewingUserResponse, listen to activityRecipients instead');
  }

  getTitle(): string {
    return this.get('title');
  }

  getWelcomeMsgBody(): ?string {
    return this.get('welcome_msg_body');
  }

  getLocation(): ?string {
    return this.get('location');
  }

  getCreatedAt(): ?Date {
    return this.get('created_at');
  }

  getStartAt(): ?Date {
    return this.get('start_at');
  }

  getEndAt(): ?Date {
    return this.get('end_at');
  }

  getFee(): number {
    return this.get('fee');
  }

  getRecipientSummary(): RecipientSummary {
    return this.get('recipient_summary');
  }

  getChatUuid(): ?string {
    let uuid = null;
    if (this.get('chat')) {
      uuid = this.get('chat').uuid;
    }
    return uuid;
  }

  // typecheck: ignore statement
  static create(params: ActivityCreateParams) {
    return apiRequest('/activities', 'POST', {
      activity: {
        title: params.title,
        amount_per_person: params.amountPerPerson,
        location: params.location,
        has_children: params.hasChildren,
        start_at: params.date,
        end_at: params.endDate,
        group_ids: params.groupIds,
        collect_online: params.collectOnline,
        recipient_ids: params.individualIds,
        disable_replies: params.disableReplies,
        welcome_msg_body: params.welcomeMsgBody,
        event_type: params.type,
        targets: params.targets,
      },
    }).then(request => Activity.fromJSON(request));
  }

  parse(response: Object): Object {
    const request = cloneDeep(response);

    if ('created_at' in request && request['created_at'] !== null) {
      request['created_at'] = new Date(request['created_at']);
    }

    if ('start_at' in request && request['start_at'] !== null) {
      request['start_at'] = new Date(request['start_at']);
    }

    if ('end_at' in request && request['end_at'] !== null) {
      request['end_at'] = new Date(request['end_at']);
    }

    if ('requester' in request) {
      request['requester'] = RelatedUserSummary.fromJSON(request['requester']);
    }

    if ('viewing_user_response' in request) {
      request['viewing_user_response'] = ActivityRecipient.fromJSON(
        request['viewing_user_response']
      );
    }

    if ('recipient_summary' in request) {
      request['recipient_summary'] = RecipientSummary.fromJSON(request['recipient_summary']);
    }

    return request;
  }

  // typecheck: ignore statement
  static edit(params: ActivityEditParams): Promise<Activity> {
    return apiRequest(`/activities/${params.activity.id}`, 'PATCH', {
      activity: {
        amount_per_person: params.amountPerPerson,
        welcome_msg_body: params.welcomeMsgBody,
        collect_online: params.collectOnline,
        has_children: params.hasChildren,
        location: params.location,
        end_at: params.endDate,
        start_at: params.date,
        title: params.title,
      },
    }).then(response => Activity.fromJSON(response));
  }

  static getCurrentUserRecipientResponse(activityUuid): Promise<ActivityRecipient> {
    return apiRequest(`/activities/${activityUuid}/recipient_response`, 'GET').then(
      response => ActivityRecipient.fromJSON(response)
    );
  }

  static getRecipients(activityId: string): Promise<ActivityRecipients> {
    return apiRequest(`/activities/${activityId}/recipients`, 'GET').then(
      response => ActivityRecipients.fromJSON(response.recipients ? response.recipients : response)
    );
  }

  static export(activityId: string) {
    const tz = jstz.determine();
    const timezone = tz.name();
    return apiRequest(`/activities/${activityId}/export`, 'POST', {
      time_zone: timezone,
    });
  }

  close(): Promise<Activity> {
    return apiRequest(`/activities/${this.id}`, 'PATCH', {
      activity: { state: 'closed' },
    }).then(response => Activity.fromJSON(response));
  }

  createRSVP(opts: Object): Promise<ActivityRecipient> {
    return (
      apiRequest(`/activities/${this.id}/recipient_response`, 'POST', opts)
      .then(response => ActivityRecipient.fromJSON(response))
    );
  }

  static findByUuid(requestUuid): Promise<Activity> {
    return apiRequest(`/activities/${requestUuid}`, 'GET').then(
      response => Activity.fromJSON(response)
    );
  }
}

export default Activity;



// WEBPACK FOOTER //
// ./src/entities/Activity.js